import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faTimesCircle,
  faClockThree,
  faCalendarXmark,
  faMegaphone,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { MonitoringStatus } from 'enum/monitoring-status.enum'
import { CourseStateType, EnrolState, EnrolEvaluationState } from 'enum'
import { ICourseCard } from 'models/courseCard.model'
import { IEnrolCard } from 'models/enrolCard.model'
import { IMainState } from 'store/states'
import { synchronousTypes } from './constants'

interface TagsProps {
  course: ICourseCard
  enrol: IEnrolCard | null
}

export * from './constants'

export function useTags({ course, enrol }: TagsProps) {
  const t = useTranslationRankmi({
    prefix: 'components.courseCard',
  })
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )

  const { synchronousCourseType } = { ...course?.synchronousCourse }

  const synchronousType =
    synchronousCourseType && synchronousTypes[synchronousCourseType]

  const state = course.state
  const imparted = state === CourseStateType.Imparted
  const inactive = state === CourseStateType.Inactive
  const soon = state === CourseStateType.Soon
  const finished = enrol?.advance === MonitoringStatus.Finish

  const tags = useMemo(
    () =>
      [
        soon && (
          <>
            <FontAwesomeIcon icon={faMegaphone} className="mr-2" />
            {t('states.soon')}
          </>
        ),
        inactive && (
          <>
            <FontAwesomeIcon icon={faCalendarXmark} className="mr-2" />
            {t('states.inactive-new')}
          </>
        ),
        imparted && !finished && !inactive && (
          <>
            <FontAwesomeIcon icon={faCalendarXmark} className="mr-2" />
            {t('states.imparted-new')}
          </>
        ),
        (enrol?.evaluationState === EnrolEvaluationState.Approved ||
          enrol?.evaluationState === EnrolEvaluationState.Failed) && (
          <>
            {enrol?.evaluationState === EnrolEvaluationState.Approved ? (
              <>
                <FontAwesomeIcon
                  className="mr-1"
                  size={'xs'}
                  icon={faCheckCircle}
                  color="#60D9A6"
                />
                {t('approved')}
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  className="mr-1"
                  size={'xs'}
                  icon={faTimesCircle}
                  color="#F58383"
                />
                {t('notApproved')}
              </>
            )}
          </>
        ),
        !!(finished && course.synchronousCourse && enrol) && (
          <>
            {(enrol.synchronousEnrolFinalState === EnrolState.Pending ||
              enrol.synchronousEnrolFinalState === null) && (
              <>
                <FontAwesomeIcon
                  className="mr-1"
                  size={'xs'}
                  icon={faClockThree}
                  color="#FFC400"
                />
                {t('pending')}
              </>
            )}
            {enrol.synchronousEnrolFinalState === EnrolState.Approved && (
              <>
                <FontAwesomeIcon
                  className="mr-1"
                  size={'xs'}
                  icon={faCheckCircle}
                  color="#60D9A6"
                />
                {t('approved')}
              </>
            )}
            {(enrol.synchronousEnrolFinalState === EnrolState.Denied ||
              enrol.synchronousEnrolFinalState === EnrolState.Absent) && (
              <>
                <FontAwesomeIcon
                  className="mr-1"
                  size={'xs'}
                  icon={faTimesCircle}
                  color="#F58383"
                />
                {t('notApproved')}
              </>
            )}
          </>
        ),
        attestationsEnabled && course.normative && <>{t('normative')}</>,
        course.mandatory && <>{t('mandatory')}</>,
        synchronousType && !!course.synchronousCourse && (
          <>
            <FontAwesomeIcon
              size="1x"
              icon={synchronousType.icon}
              className="mr-1"
            />
            {t(synchronousType.title)}
          </>
        ),
      ].filter(Boolean),
    [
      attestationsEnabled,
      course.mandatory,
      course.normative,
      course.synchronousCourse,
      enrol,
      finished,
      imparted,
      soon,
      synchronousType,
      t,
    ]
  )

  return tags
}
